import { db } from "@/firebase";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  serverTimestamp,
  orderBy,
  updateDoc,
  addDoc
} from "firebase/firestore";
import { logDebug, logInfo, logError } from "@/utils/secureLogger";

export interface ConversationData {
  id: string;
  studentId: string;
  tutorId: string;
  studentName: string;
  tutorName: string;
  createdAt: any;
  lastMessageAt?: any;
  lastMessage?: string;
  sessionId?: string; // Link to the session that created this conversation
  status: 'active' | 'archived';
}

export interface SessionRelationship {
  hasActiveSession: boolean;
  sessionId?: string;
  sessionStatus?: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  sessionSubject?: string;
  sessionDate?: string;
}

class ConversationService {
  private conversationsCollection = collection(db, "conversations");

  /**
   * Check if a student can message a tutor
   * Students can message any approved tutor
   */
  async canStudentMessageTutor(studentId: string, tutorId: string): Promise<{
    canMessage: boolean;
    reason?: string;
    tutorData?: any;
  }> {
    try {
      logDebug('Checking if student can message tutor', { studentId, tutorId });
      
      // Get tutor data to check if they're approved
      const tutorDoc = await getDoc(doc(db, "users", tutorId));
      if (!tutorDoc.exists()) {
        return {
          canMessage: false,
          reason: "Tutor not found"
        };
      }

      const tutorData = tutorDoc.data();
      
      // Check if tutor is approved
      if (tutorData.profileStatus !== 'approved') {
        return {
          canMessage: false,
          reason: "Tutor is not yet approved"
        };
      }

      // Students can always message approved tutors
      return {
        canMessage: true,
        tutorData
      };
    } catch (error) {
      logError('Error checking student messaging permission', error);
      return {
        canMessage: false,
        reason: "Error checking permissions"
      };
    }
  }

  /**
   * Check if a tutor can message a student
   * Tutors can only message students with pending or confirmed sessions
   */
  async canTutorMessageStudent(tutorId: string, studentId: string): Promise<{
    canMessage: boolean;
    reason?: string;
    sessionRelationship?: SessionRelationship;
    studentData?: any;
  }> {
    try {
      logDebug('Checking if tutor can message student', { tutorId, studentId });
      
      // Get student data
      const studentDoc = await getDoc(doc(db, "users", studentId));
      if (!studentDoc.exists()) {
        return {
          canMessage: false,
          reason: "Student not found"
        };
      }

      const studentData = studentDoc.data();

      // Check for active sessions between this tutor and student
      const sessionsRef = collection(db, "sessions");
      const sessionsQuery = query(
        sessionsRef,
        where("tutorId", "==", tutorId),
        where("studentId", "==", studentId),
        where("status", "==", "active")
      );

      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      if (sessionsSnapshot.empty) {
        return {
          canMessage: false,
          reason: "No active sessions with this student",
          studentData
        };
      }

      // Check if there are any pending or confirmed sessions
      const sessions = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[]; // Type assertion for Firestore document data

      const activeSessions = sessions.filter((session: any) => 
        session.sessionStatus === 'pending' || session.sessionStatus === 'confirmed'
      );

      if (activeSessions.length === 0) {
        return {
          canMessage: false,
          reason: "No pending or confirmed sessions with this student",
          studentData,
          sessionRelationship: {
            hasActiveSession: false
          }
        };
      }

      // Get the most recent active session
      const mostRecentSession = activeSessions.sort((a: any, b: any) => 
        b.createdAt?.toDate().getTime() - a.createdAt?.toDate().getTime()
      )[0];

      return {
        canMessage: true,
        studentData,
        sessionRelationship: {
          hasActiveSession: true,
          sessionId: mostRecentSession.id,
          sessionStatus: mostRecentSession.sessionStatus,
          sessionSubject: mostRecentSession.subject,
          sessionDate: mostRecentSession.startAt?.toDate().toLocaleDateString()
        }
      };
    } catch (error) {
      logError('Error checking tutor messaging permission', error);
      return {
        canMessage: false,
        reason: "Error checking permissions"
      };
    }
  }

  /**
   * Create a conversation between student and tutor
   * This is called when the message button is clicked on a session card
   */
  async createConversation(
    studentId: string, 
    tutorId: string, 
    sessionId?: string
  ): Promise<{
    success: boolean;
    conversationId?: string;
    reason?: string;
  }> {
    try {
      logInfo('Creating conversation', { studentId, tutorId, sessionId });
      
      // Check if conversation already exists
      const existingConversation = await this.getExistingConversation(studentId, tutorId);
      if (existingConversation) {
        logInfo('Using existing conversation', { 
          conversationId: existingConversation.id, 
          studentId, 
          tutorId 
        });
        return {
          success: true,
          conversationId: existingConversation.id,
          reason: "Conversation already exists"
        };
      }

      // Get user names for the conversation
      const [studentDoc, tutorDoc] = await Promise.all([
        getDoc(doc(db, "users", studentId)),
        getDoc(doc(db, "users", tutorId))
      ]);

      if (!studentDoc.exists() || !tutorDoc.exists()) {
        return {
          success: false,
          reason: "User not found"
        };
      }

      const studentData = studentDoc.data();
      const tutorData = tutorDoc.data();

      const studentName = `${studentData.firstName} ${studentData.lastName}`.trim();
      const tutorName = `${tutorData.firstName} ${tutorData.lastName}`.trim();

      // Create conversation document (not tied to specific session)
      const conversationData = {
        studentId,
        tutorId,
        studentName,
        tutorName,
        createdAt: serverTimestamp(),
        status: 'active'
        // Remove sessionId to avoid creating multiple conversations for same student-tutor pair
        // ...(sessionId && { sessionId })
      };

      const docRef = await addDoc(this.conversationsCollection, conversationData);
      
      logInfo('Conversation created successfully', { conversationId: docRef.id });
      
      return {
        success: true,
        conversationId: docRef.id
      };
    } catch (error) {
      logError('Error creating conversation', error);
      return {
        success: false,
        reason: "Failed to create conversation"
      };
    }
  }

  /**
   * Get existing conversation between student and tutor
   */
  async getExistingConversation(studentId: string, tutorId: string): Promise<ConversationData | null> {
    try {
      // Query for conversations where studentId and tutorId match (in either direction)
      const conversationsQuery1 = query(
        this.conversationsCollection,
        where("studentId", "==", studentId),
        where("tutorId", "==", tutorId),
        where("status", "==", "active")
      );

      const conversationsQuery2 = query(
        this.conversationsCollection,
        where("studentId", "==", tutorId),
        where("tutorId", "==", studentId),
        where("status", "==", "active")
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(conversationsQuery1),
        getDocs(conversationsQuery2)
      ]);
      
      // Check first query result
      if (!snapshot1.empty) {
        const conversation = snapshot1.docs[0];
        return {
          id: conversation.id,
          ...conversation.data()
        } as ConversationData;
      }
      
      // Check second query result
      if (!snapshot2.empty) {
        const conversation = snapshot2.docs[0];
        return {
          id: conversation.id,
          ...conversation.data()
        } as ConversationData;
      }

      return null;
    } catch (error) {
      logError('Error getting existing conversation', error);
      return null;
    }
  }

  /**
   * Get all conversations for a student
   */
  async getStudentConversations(studentId: string): Promise<ConversationData[]> {
    try {
      const conversationsQuery = query(
        this.conversationsCollection,
        where("studentId", "==", studentId),
        where("status", "==", "active"),
        orderBy("lastMessageAt", "desc")
      );

      const snapshot = await getDocs(conversationsQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ConversationData[];
    } catch (error) {
      logError('Error getting student conversations', error);
      return [];
    }
  }

  /**
   * Get all conversations for a tutor (only with students they have sessions with)
   */
  async getTutorConversations(tutorId: string): Promise<ConversationData[]> {
    try {
      const conversationsQuery = query(
        this.conversationsCollection,
        where("tutorId", "==", tutorId),
        where("status", "==", "active"),
        orderBy("lastMessageAt", "desc")
      );

      const snapshot = await getDocs(conversationsQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ConversationData[];
    } catch (error) {
      logError('Error getting tutor conversations', error);
      return [];
    }
  }

  /**
   * Update conversation with last message info
   */
  async updateConversationLastMessage(
    conversationId: string, 
    message: string
  ): Promise<boolean> {
    try {
      const conversationRef = doc(this.conversationsCollection, conversationId);
      await updateDoc(conversationRef, {
        lastMessage: message,
        lastMessageAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      logError('Error updating conversation last message', error);
      return false;
    }
  }

  /**
   * Get session-based messaging permissions for UI display
   */
  async getMessagingPermissions(
    currentUserId: string,
    targetUserId: string,
    currentUserType: 'student' | 'tutor'
  ): Promise<{
    canMessage: boolean;
    reason?: string;
    sessionInfo?: SessionRelationship;
  }> {
    try {
      if (currentUserType === 'student') {
        const result = await this.canStudentMessageTutor(currentUserId, targetUserId);
        return {
          canMessage: result.canMessage,
          reason: result.reason
        };
      } else {
        const result = await this.canTutorMessageStudent(currentUserId, targetUserId);
        return {
          canMessage: result.canMessage,
          reason: result.reason,
          sessionInfo: result.sessionRelationship
        };
      }
    } catch (error) {
      logError('Error getting messaging permissions', error);
      return {
        canMessage: false,
        reason: "Error checking permissions"
      };
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let conversationServiceInstance: ConversationService | null = null;

export function getConversationService(): ConversationService {
  if (!conversationServiceInstance) {
    conversationServiceInstance = new ConversationService();
  }
  return conversationServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const conversationService = new Proxy({} as ConversationService, {
  get(target, prop) {
    return getConversationService()[prop as keyof ConversationService];
  }
});
export default ConversationService;
